import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { SettingsProvider } from "@/context/SettingsContext";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

import Layout from "@/components/layout/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Product from "@/pages/Product";
import { LookbookList, LookbookDetail } from "@/pages/Lookbook";
import ContentPage from "@/pages/ContentPage";
import Contact from "@/pages/Contact";
import Checkout from "@/pages/Checkout";
import CheckoutReturn from "@/pages/CheckoutReturn";
import AccountIndex from "@/pages/account/AccountIndex";
import AccountDashboard from "@/pages/account/AccountDashboard";
import AuthCallback from "@/pages/account/AuthCallback";
import { ForgotPassword, ResetPassword } from "@/pages/account/ForgotReset";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminHomepage from "@/pages/admin/AdminHomepage";
import AdminMedia from "@/pages/admin/AdminMedia";
import AdminSettings from "@/pages/admin/AdminSettings";
import { AdminCollections, AdminLookbooks, AdminPages, AdminDiscounts, AdminReviews, AdminCustomers } from "@/pages/admin/AdminMisc";
import { MarketingOverview, MarketingCampaigns, MarketingSegments, MarketingTemplates, MarketingAutomations, MarketingAnalytics, MarketingContacts } from "@/pages/admin/AdminMarketing";

function NotFound() {
  return (
    <div className="onusa-container py-40 text-center" data-testid="not-found">
      <p className="label-x">404</p>
      <h1 className="heading-x text-4xl mt-3">PAGINA NIET GEVONDEN</h1>
      <a href="/" className="btn-x-outline mt-8 inline-flex">TERUG NAAR ONUSA</a>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/product/:slug" element={<Product />} />
        <Route path="/collections/:slug" element={<Shop />} />
        <Route path="/lookbook" element={<LookbookList />} />
        <Route path="/lookbook/:slug" element={<LookbookDetail />} />
        <Route path="/about" element={<ContentPage slug="about" />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<ContentPage slug="faq" />} />
        <Route path="/shipping" element={<ContentPage slug="shipping" />} />
        <Route path="/returns" element={<ContentPage slug="returns" />} />
        <Route path="/size-guide" element={<ContentPage slug="size-guide" />} />
        <Route path="/privacy" element={<ContentPage slug="privacy" />} />
        <Route path="/cookies" element={<ContentPage slug="cookies" />} />
        <Route path="/terms" element={<ContentPage slug="terms" />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/return" element={<CheckoutReturn />} />
        <Route path="/account" element={<AccountIndex />} />
        <Route path="/account/orders" element={<ProtectedRoute><AccountDashboard /></ProtectedRoute>} />
        <Route path="/account/wishlist" element={<ProtectedRoute><AccountDashboard /></ProtectedRoute>} />
        <Route path="/account/forgot" element={<ForgotPassword />} />
        <Route path="/account/reset" element={<ResetPassword />} />
      </Route>

      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminDashboard />} />
        <Route path="products" element={<AdminProducts />} />
        <Route path="orders" element={<AdminOrders />} />
        <Route path="support" element={<AdminInbox />} />
        <Route path="homepage" element={<AdminHomepage />} />
        <Route path="collections" element={<AdminCollections />} />
        <Route path="lookbooks" element={<AdminLookbooks />} />
        <Route path="pages" element={<AdminPages />} />
        <Route path="media" element={<AdminMedia />} />
        <Route path="discounts" element={<AdminDiscounts />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="marketing" element={<MarketingOverview />} />
        <Route path="marketing/contacts" element={<MarketingContacts />} />
        <Route path="marketing/campaigns" element={<MarketingCampaigns />} />
        <Route path="marketing/segments" element={<MarketingSegments />} />
        <Route path="marketing/templates" element={<MarketingTemplates />} />
        <Route path="marketing/automations" element={<MarketingAutomations />} />
        <Route path="marketing/analytics" element={<MarketingAnalytics />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SettingsProvider>
        <AuthProvider>
          <CartProvider>
            <AppRouter />
            <Toaster position="bottom-right" toastOptions={{ style: { borderRadius: 0, fontFamily: "Space Mono, monospace", fontSize: "0.72rem", letterSpacing: "0.05em" } }} />
          </CartProvider>
        </AuthProvider>
      </SettingsProvider>
    </BrowserRouter>
  );
}
